// 1) utils: labelLayout.ts


// labelLayout.ts
import type { CSSProperties } from 'react';

export type Align = 'left' | 'center' | 'right';

export type LayoutField = {
  xMm: number;
  yMm: number;
  wMm: number;
  hMm: number;

  fontPt?: number;
  bold?: boolean;
  align?: Align;
  hidden?: boolean;
};

export type BarcodeField = LayoutField & {
  format?: 'CODE128' | 'EAN13' | 'CODE39';
  barWidthPx?: number;
  showValue?: boolean;
};

export type LabelLayoutConfig = {
  labelWmm: number;
  labelHmm: number;
  gapMm: number;

  fields: {
    name: LayoutField;
    price: LayoutField;
    uom: LayoutField;
    code: LayoutField;
    barcode: BarcodeField;
    barcodeText: LayoutField;
  };
};

export type FieldKey = keyof LabelLayoutConfig['fields'];

export const FIELD_OPTIONS: { key: FieldKey; label: string; isBarcode?: boolean }[] = [
  { key: 'name', label: 'اسم المنتج' },
  { key: 'price', label: 'السعر' },
  { key: 'uom', label: 'الوحدة' },
  { key: 'code', label: 'كود الصنف' },
  { key: 'barcode', label: 'الباركود', isBarcode: true },
  { key: 'barcodeText', label: 'نص الباركود' }
];

export const LS_KEY = 'ntx_barcode_label_layout_v1';

export const PX_PER_MM = 3.78; // تقريب على 96dpi
export const ptToPx = (ptVal: number) => ptVal * (96 / 72);

export const mm = (n: number) => `${n}mm`;
export const clamp0 = (n: number) => (Number.isFinite(n) ? Math.max(0, n) : 0);

// mm styles (print)
export const fieldStyleMM = (f: LayoutField): CSSProperties => ({
  position: 'absolute',
  left: mm(clamp0(f.xMm)),
  top: mm(clamp0(f.yMm)),
  width: mm(clamp0(f.wMm)),
  height: mm(clamp0(f.hMm)),
  overflow: 'hidden',
  textAlign: f.align ?? 'center',
  fontSize: f.fontPt ? `${f.fontPt}pt` : undefined,
  fontWeight: f.bold ? 800 : 500,
  lineHeight: 1.1,
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis'
});

// px styles (preview)
export const fieldStylePX = (f: LayoutField, scale: number): CSSProperties => ({
  position: 'absolute',
  left: `${clamp0(f.xMm) * PX_PER_MM * scale}px`,
  top: `${clamp0(f.yMm) * PX_PER_MM * scale}px`,
  width: `${clamp0(f.wMm) * PX_PER_MM * scale}px`,
  height: `${clamp0(f.hMm) * PX_PER_MM * scale}px`,
  overflow: 'hidden',
  textAlign: f.align ?? 'center',
  fontSize: f.fontPt ? `${ptToPx(f.fontPt) * scale}px` : `${12 * scale}px`,
  fontWeight: f.bold ? 800 : 500,
  lineHeight: 1.1,
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis'
});

// Presets
export const presetRoll80x40 = (): LabelLayoutConfig => ({
  labelWmm: 80,
  labelHmm: 40,
  gapMm: 2,
  fields: {
    name: { xMm: 2, yMm: 2, wMm: 76, hMm: 8, fontPt: 12, bold: true, align: 'center' },
    price: { xMm: 2, yMm: 11, wMm: 76, hMm: 6, fontPt: 11, bold: true, align: 'center' },
    uom: { xMm: 2, yMm: 18, wMm: 76, hMm: 5, fontPt: 10, align: 'center', hidden: true },
    code: { xMm: 2, yMm: 18, wMm: 76, hMm: 5, fontPt: 9, align: 'center', hidden: true },
    barcode: {
      xMm: 2,
      yMm: 24,
      wMm: 76,
      hMm: 12,
      format: 'CODE128',
      barWidthPx: 1.4,
      showValue: false,
      align: 'center'
    },
    barcodeText: { xMm: 2, yMm: 36.5, wMm: 76, hMm: 3, fontPt: 9, align: 'center' }
  }
});

export const presetA4_63x38 = (): LabelLayoutConfig => ({
  labelWmm: 63,
  labelHmm: 38,
  gapMm: 2,
  fields: {
    name: { xMm: 2, yMm: 2, wMm: 59, hMm: 8, fontPt: 11, bold: true, align: 'center' },
    price: { xMm: 2, yMm: 11, wMm: 59, hMm: 6, fontPt: 10, bold: true, align: 'center' },
    uom: { xMm: 2, yMm: 18, wMm: 59, hMm: 5, fontPt: 9, align: 'center', hidden: true },
    code: { xMm: 2, yMm: 18, wMm: 59, hMm: 5, fontPt: 9, align: 'center', hidden: true },
    barcode: {
      xMm: 2,
      yMm: 24,
      wMm: 59,
      hMm: 11,
      format: 'CODE128',
      barWidthPx: 1.2,
      showValue: false,
      align: 'center'
    },
    barcodeText: { xMm: 2, yMm: 35.5, wMm: 59, hMm: 2.5, fontPt: 8.5, align: 'center' }
  }
});
